import React, { useState, useEffect, useCallback } from 'react';

// No fields are hard-locked — hoteliers configure all fields per their property policy
const LEGALLY_REQUIRED = new Set([]);
const DEFAULT_POLICY_CONFIG = {
  domestic_document_mode: 'EACH_GUEST',
  require_dob_or_age_per_guest: true,
  capture_id_expiry_if_applicable: true,
  nationality_source: 'COUNTRY_MASTER',
  default_nationality_to_hotel_country: true,
  require_id_document_upload: false,
  require_passport_upload: false
};

function PmsCompliancePolicy({ token, selectedProperty }) {
  const [rules, setRules] = useState([]);
  const [policyConfig, setPolicyConfig] = useState(DEFAULT_POLICY_CONFIG);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text }

  // REQ-09 (docs/SRS_AMBERPARAMOUNT_REQ08-13.md) — configurable ID Type master
  const [idTypes, setIdTypes] = useState([]);
  const [idTypesLoading, setIdTypesLoading] = useState(false);
  const [idTypeMessage, setIdTypeMessage] = useState(null);
  const [newIdType, setNewIdType] = useState({ label: '', applies_to: 'DOMESTIC' });
  const [savingIdType, setSavingIdType] = useState(false);

  const chainId = selectedProperty?.chain_id;
  const propertyId = selectedProperty?.id;
  const propertyName = selectedProperty?.name || '';

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'x-chain-id': chainId,
    'x-property-id': propertyId,
  };

  const fetchRules = useCallback(async () => {
    if (!chainId || !propertyId) return;
    setLoading(true);
    try {
      const [rulesRes, configRes] = await Promise.all([
        fetch('/api/v1/compliance/fields', { headers }),
        fetch('/api/v1/compliance/policy/guest-identity-document-rules', { headers })
      ]);
      const [rulesData, configData] = await Promise.all([
        rulesRes.json().catch(() => []),
        configRes.json().catch(() => ({}))
      ]);
      if (Array.isArray(rulesData)) setRules(rulesData);
      if (configRes.ok && configData && typeof configData === 'object') {
        setPolicyConfig({
          domestic_document_mode: configData.domestic_document_mode || DEFAULT_POLICY_CONFIG.domestic_document_mode,
          require_dob_or_age_per_guest: Boolean(
            configData.require_dob_or_age_per_guest ?? DEFAULT_POLICY_CONFIG.require_dob_or_age_per_guest
          ),
          capture_id_expiry_if_applicable: Boolean(
            configData.capture_id_expiry_if_applicable ?? DEFAULT_POLICY_CONFIG.capture_id_expiry_if_applicable
          ),
          nationality_source: configData.nationality_source || DEFAULT_POLICY_CONFIG.nationality_source,
          default_nationality_to_hotel_country: Boolean(
            configData.default_nationality_to_hotel_country ?? DEFAULT_POLICY_CONFIG.default_nationality_to_hotel_country
          ),
          require_id_document_upload: Boolean(
            configData.require_id_document_upload ?? DEFAULT_POLICY_CONFIG.require_id_document_upload
          ),
          require_passport_upload: Boolean(
            configData.require_passport_upload ?? DEFAULT_POLICY_CONFIG.require_passport_upload
          )
        });
      } else {
        setPolicyConfig(DEFAULT_POLICY_CONFIG);
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to load compliance fields.' });
    } finally {
      setLoading(false);
    }
  }, [chainId, propertyId, token]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const fetchIdTypes = useCallback(async () => {
    if (!chainId || !propertyId) return;
    setIdTypesLoading(true);
    try {
      const res = await fetch('/api/v1/pms/id-types', { headers });
      const data = await res.json().catch(() => []);
      setIdTypes(Array.isArray(data) ? data : []);
    } catch {
      setIdTypeMessage({ type: 'error', text: 'Failed to load ID types.' });
    } finally {
      setIdTypesLoading(false);
    }
  }, [chainId, propertyId, token]);

  useEffect(() => { fetchIdTypes(); }, [fetchIdTypes]);

  const addIdType = async () => {
    const label = newIdType.label.trim();
    if (!label) return;
    setSavingIdType(true);
    setIdTypeMessage(null);
    try {
      const res = await fetch('/api/v1/pms/id-types', {
        method: 'POST', headers,
        body: JSON.stringify({ code: label, label, applies_to: newIdType.applies_to })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setIdTypeMessage({ type: 'error', text: data.error || 'Failed to add ID type' }); return; }
      setNewIdType({ label: '', applies_to: 'DOMESTIC' });
      await fetchIdTypes();
    } catch {
      setIdTypeMessage({ type: 'error', text: 'Network error adding ID type.' });
    } finally {
      setSavingIdType(false);
    }
  };

  const updateIdType = async (idType, patch) => {
    setIdTypeMessage(null);
    try {
      const res = await fetch(`/api/v1/pms/id-types/${idType.id}`, {
        method: 'PUT', headers, body: JSON.stringify(patch)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setIdTypeMessage({ type: 'error', text: data.error || 'Update failed' }); return; }
      await fetchIdTypes();
    } catch {
      setIdTypeMessage({ type: 'error', text: 'Network error updating ID type.' });
    }
  };

  const deleteIdType = async (idType) => {
    if (!window.confirm(`Delete ID type "${idType.label}"?`)) return;
    setIdTypeMessage(null);
    try {
      const res = await fetch(`/api/v1/pms/id-types/${idType.id}`, { method: 'DELETE', headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setIdTypeMessage({ type: 'error', text: data.error || 'Delete failed' }); return; }
      await fetchIdTypes();
    } catch {
      setIdTypeMessage({ type: 'error', text: 'Network error deleting ID type.' });
    }
  };

  const toggle = (index, field) => {
    setRules((prev) => prev.map((r, i) => {
      if (i !== index) return r;
      if (LEGALLY_REQUIRED.has(r.field_name)) return r; // locked
      const updated = { ...r, [field]: !r[field] };
      // If not visible, also unset mandatory
      if (field === 'is_visible' && !updated.is_visible) updated.is_mandatory = false;
      return updated;
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const payload = rules.map((r) => ({
        field_name: r.field_name,
        is_mandatory: r.is_mandatory,
        is_visible: r.is_visible,
      }));
      const [fieldsRes, configRes] = await Promise.all([
        fetch('/api/v1/compliance/fields', {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        }),
        fetch('/api/v1/compliance/policy/guest-identity-document-rules', {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            domestic_document_mode: policyConfig.domestic_document_mode,
            require_dob_or_age_per_guest: policyConfig.require_dob_or_age_per_guest,
            capture_id_expiry_if_applicable: policyConfig.capture_id_expiry_if_applicable,
            default_nationality_to_hotel_country: policyConfig.default_nationality_to_hotel_country,
            require_id_document_upload: policyConfig.require_id_document_upload,
            require_passport_upload: policyConfig.require_passport_upload
          })
        })
      ]);
      if (fieldsRes.ok && configRes.ok) {
        setMessage({ type: 'success', text: 'Compliance policy saved successfully.' });
      } else {
        const fieldErr = await fieldsRes.json().catch(() => ({}));
        const configErr = await configRes.json().catch(() => ({}));
        setMessage({ type: 'error', text: fieldErr.error || configErr.error || 'Save failed.' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Network error saving policy.' });
    } finally {
      setSaving(false);
    }
  };

  const updatePolicyConfig = (patch) => {
    setPolicyConfig((prev) => ({ ...prev, ...patch }));
  };

  const renderPolicyRulesCard = () => (
    <div className="card" style={{ marginBottom: '1.5rem' }}>
      <div className="card-body">
        <div className="section-head" style={{ marginBottom: '0.75rem' }}>Guest Identity &amp; Document Collection Rules</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: '1rem' }}>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)' }}>Domestic Document Mode</span>
            <select
              value={policyConfig.domestic_document_mode}
              onChange={(e) => updatePolicyConfig({ domestic_document_mode: e.target.value })}
              style={{ width: '100%', padding: '8px' }}
            >
              <option value='PRIMARY_GUEST_ONLY'>Primary Guest Only</option>
              <option value='EACH_GUEST'>Each Guest</option>
            </select>
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)' }}>Nationality Source</span>
            <input value={policyConfig.nationality_source || 'COUNTRY_MASTER'} readOnly style={{ width: '100%', padding: '8px', background: '#f3f4f6' }} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem', fontWeight: 600, color: '#9ca3af', cursor: 'default' }}>
            <input
              type='checkbox'
              checked={true}
              disabled
            />
            Date of Birth mandatory per Guest
            <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: 400 }}>(always required)</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem', fontWeight: 600, color: 'var(--theme-text)', cursor: 'pointer' }}>
            <input
              type='checkbox'
              checked={Boolean(policyConfig.capture_id_expiry_if_applicable)}
              onChange={(e) => updatePolicyConfig({ capture_id_expiry_if_applicable: e.target.checked })}
            />
            Capture ID Expiry (if applicable)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem', fontWeight: 600, color: 'var(--theme-text)', cursor: 'pointer' }}>
            <input
              type='checkbox'
              checked={Boolean(policyConfig.default_nationality_to_hotel_country)}
              onChange={(e) => updatePolicyConfig({ default_nationality_to_hotel_country: e.target.checked })}
            />
            Default Nationality to Hotel Country
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem', fontWeight: 600, color: 'var(--theme-text)', cursor: 'pointer' }}>
            <input
              type='checkbox'
              checked={Boolean(policyConfig.require_id_document_upload)}
              onChange={(e) => updatePolicyConfig({ require_id_document_upload: e.target.checked })}
            />
            Require ID Document Upload (Domestic Guests)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem', fontWeight: 600, color: 'var(--theme-text)', cursor: 'pointer' }}>
            <input
              type='checkbox'
              checked={Boolean(policyConfig.require_passport_upload)}
              onChange={(e) => updatePolicyConfig({ require_passport_upload: e.target.checked })}
            />
            Require Passport Upload (Foreign Guests)
          </label>
        </div>
      </div>
    </div>
  );

  // REQ-09 — configurable ID Type master. Domestic ID type options shown at
  // check-in (PmsComplianceCheckin.js) are driven by this list, filtered to
  // applies_to IN ('DOMESTIC','ALL'); foreign guests keep their separate,
  // dedicated passport fields untouched.
  const renderIdTypesCard = () => (
    <div className="card" style={{ marginBottom: '1.5rem' }}>
      <div className="card-body">
        <div className="section-head" style={{ marginBottom: '0.5rem' }}>ID Types (Domestic Check-In)</div>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: '0.75rem' }}>
          Configure which ID types staff can select for domestic guests at check-in. Foreign guests continue to use passport capture, unaffected here.
        </div>
        {idTypeMessage && (
          <div className={idTypeMessage.type === 'success' ? 'success-banner' : 'error-banner'} style={{ marginBottom: '0.6rem' }}>
            {idTypeMessage.text}
          </div>
        )}
        {idTypesLoading ? <div style={{ color: '#6b7280' }}>Loading…</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ fontSize: 13, width: '100%' }}>
              <thead>
                <tr style={{ background: 'var(--theme-primary-dark)' }}>
                  <th style={th}>Label</th>
                  <th style={{ ...th, width: 140 }}>Applies To</th>
                  <th style={{ ...th, textAlign: 'center', width: 90 }}>Active</th>
                  <th style={{ ...th, width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {idTypes.map((t, i) => (
                  <tr key={t.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={td}>
                      <input defaultValue={t.label}
                        onBlur={(e) => { const val = e.target.value.trim(); if (val && val !== t.label) updateIdType(t, { label: val }); }}
                        style={{ width: '100%', padding: '4px 6px', border: '1px solid #e5e7eb', borderRadius: 4 }} />
                      {t.is_system && <span style={{ fontSize: 10, color: '#9ca3af', marginLeft: 6 }}>(system)</span>}
                    </td>
                    <td style={td}>
                      <select value={t.applies_to} onChange={(e) => updateIdType(t, { applies_to: e.target.value })} style={{ padding: '4px 6px', width: '100%' }}>
                        <option value='DOMESTIC'>Domestic</option>
                        <option value='FOREIGN'>Foreign</option>
                        <option value='ALL'>All</option>
                      </select>
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <Toggle checked={t.is_active} disabled={t.is_system} onChange={() => updateIdType(t, { is_active: !t.is_active })} />
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      {!t.is_system && (
                        <button onClick={() => deleteIdType(t)} title="Delete" style={{ border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14 }}>🗑</button>
                      )}
                    </td>
                  </tr>
                ))}
                <tr style={{ background: '#f9fafb' }}>
                  <td style={td}>
                    <input placeholder="New ID type label" value={newIdType.label}
                      onChange={(e) => setNewIdType((prev) => ({ ...prev, label: e.target.value }))}
                      style={{ width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 4 }} />
                  </td>
                  <td style={td}>
                    <select value={newIdType.applies_to} onChange={(e) => setNewIdType((prev) => ({ ...prev, applies_to: e.target.value }))} style={{ padding: '4px 6px', width: '100%' }}>
                      <option value='DOMESTIC'>Domestic</option>
                      <option value='FOREIGN'>Foreign</option>
                      <option value='ALL'>All</option>
                    </select>
                  </td>
                  <td style={{ ...td, textAlign: 'center' }} colSpan={2}>
                    <button onClick={addIdType} disabled={savingIdType || !newIdType.label.trim()} className="btn-primary" style={{ padding: '4px 12px', fontSize: 12 }}>
                      {savingIdType ? 'Adding…' : '+ Add'}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  if (!propertyId) {
    return (
      <div className="page-shell">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', marginBottom: '0.6rem' }}>
          <span style={{ color: '#6b7280', fontWeight: 500 }}>Hotel Config</span>
          <span style={{ color: '#cbd5e0' }}>›</span>
          <span style={{ color: 'var(--theme-primary-dark)', fontWeight: 600 }}>Compliance</span>
        </div>
        <h2 style={{ margin: '0 0 16px', fontSize: 18 }}>Compliance Policy</h2>
        <div className="info-banner">
          Please select a property first from <strong>Hotel Config &gt; Select Property</strong>.
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', marginBottom: '0.6rem' }}>
        <span style={{ color: '#6b7280', fontWeight: 500 }}>Hotel Config</span>
        <span style={{ color: '#cbd5e0' }}>›</span>
        <span style={{ color: 'var(--theme-primary-dark)', fontWeight: 600 }}>Compliance</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18 }}>Check-In Compliance Policy</h2>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{propertyName}</div>
        </div>
        <button onClick={handleSave} disabled={saving || loading} className="btn-primary" style={{ padding: '0.55rem 1.4rem' }}>
          {saving ? 'Saving…' : 'Save Policy'}
        </button>
      </div>

      {message && (
        <div className={message.type === 'success' ? 'success-banner' : 'error-banner'}>
          {message.text}
        </div>
      )}

      {loading ? (
        <div style={{ color: '#6b7280' }}>Loading field rules…</div>
      ) : (
        <>
          {renderPolicyRulesCard()}
          {renderIdTypesCard()}
          <div className="card" style={{ overflowX: 'auto' }}>
            <table style={{ fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--theme-primary-dark)' }}>
                <th style={th}>Field</th>
                <th style={th}>Applies To</th>
                <th style={{ ...th, textAlign: 'center', width: 110 }}>Mandatory</th>
                <th style={{ ...th, textAlign: 'center', width: 110 }}>Visible</th>
                <th style={{ ...th, width: 70 }}></th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r, i) => {
                const locked = LEGALLY_REQUIRED.has(r.field_name);
                return (
                  <tr key={r.field_name} style={{ borderBottom: '1px solid #f3f4f6',
                    background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={td}>
                      <span style={{ fontWeight: 500 }}>{r.label || r.field_name}</span>
                      <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 6 }}>({r.field_name})</span>
                    </td>
                    <td style={td}>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10,
                        background: r.applies_to === 'FOREIGN' ? '#eff6ff' : r.applies_to === 'INDIAN' ? '#f0fdf4' : '#f3f4f6',
                        color: r.applies_to === 'FOREIGN' ? '#1d4ed8' : r.applies_to === 'INDIAN' ? '#166534' : 'var(--theme-text)' }}>
                        {r.applies_to || 'ALL'}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <Toggle checked={r.is_mandatory} disabled={locked || !r.is_visible}
                        onChange={() => toggle(i, 'is_mandatory')} />
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <Toggle checked={r.is_visible} disabled={locked}
                        onChange={() => toggle(i, 'is_visible')} />
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      {locked && (
                        <span title='Legally required — cannot be disabled'
                          style={{ fontSize: 14, color: '#9ca3af' }}>🔒</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            </table>
          </div>
        </>
      )}

      <div style={{ marginTop: 14, fontSize: 11, color: '#9ca3af' }}>
        Configure which fields are mandatory and visible during guest check-in, based on your property's compliance policy.
      </div>
    </div>
  );
}

function Toggle({ checked, disabled, onChange }) {
  return (
    <button onClick={!disabled ? onChange : undefined}
      style={{
        width: 40, height: 22, borderRadius: 11, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
        background: checked ? (disabled ? '#93c5fd' : '#2563eb') : '#d1d5db',
        position: 'relative', transition: 'background 0.15s', outline: 'none',
        opacity: disabled ? 0.6 : 1,
      }}>
      <span style={{
        position: 'absolute', top: 3, left: checked ? 20 : 3,
        width: 16, height: 16, borderRadius: '50%', background: '#fff',
        transition: 'left 0.15s', display: 'block',
      }} />
    </button>
  );
}

const th = { padding: '10px 12px', textAlign: 'left', fontSize: 12, fontWeight: 700,
  color: '#fff', textTransform: 'uppercase', letterSpacing: '0.05em' };
const td = { padding: '9px 12px', verticalAlign: 'middle' };

export default PmsCompliancePolicy;
